import { useMemo } from "react"

import { useFetchWaypointsGeojson } from "./useFetchWaypointsGeojson.jsx"
import { useAnalyseReportedRoutesWorker } from "./useAnalyseReportedRoutes.jsx"
import { useFilterMarkedRoutes } from "./useFilterMarkedRoutes.jsx"
import { useDebouncedValue } from "./useDebouncedValue.jsx"
import { reportedSelectionId } from "@/features/route/state/RouteStore.jsx"
import { nowUtc } from "@/utils/appNow"

/*
 * useReportedRoutes
 *
 * Owns the reported-track data path: fetch → analyse (worker) → filter by the
 * marked vessel → map-renderable routes. The track loads for the marked vessel
 * only (a fleet-wide fetch over months exceeds what PostGraphile can return in
 * one row); whether it is drawn follows the tree selection — its
 * "Reported track" row selects a `reported:vessel:<id>` id.
 *
 * Mirrors usePlanRoutes' role for the plan side and keeps MainComponent lean.
 */
export function useReportedRoutes({
  fromPicker,
  markedVesselNames,
  treeSelectedRouteIds,
  reportedRefreshTick,
}) {
  const selectedVesselId = useMemo(() => {
    const id = Number((markedVesselNames ?? [])[0]?.vesselId)
    return Number.isFinite(id) ? id : null
  }, [markedVesselNames])

  const switchReported =
    selectedVesselId != null &&
    (treeSelectedRouteIds ?? []).includes(reportedSelectionId(selectedVesselId))

  const reportedFetchEnabled = selectedVesselId != null

  // Coalesce all fetch inputs and debounce them together: a vessel switch moves
  // vesselId AND fromPicker (old plan route → now−7d fallback → new plan route)
  // in quick succession, so debouncing fromPicker alone still fired an extra
  // request on the transient mix (old window + new vessel). Debouncing the whole
  // input fires one fetch on the settled state.
  const fetchInput = useMemo(
    () => ({
      fromPicker,
      vesselId: selectedVesselId,
      enabled: reportedFetchEnabled,
    }),
    [fromPicker, selectedVesselId, reportedFetchEnabled],
  )
  const debouncedInput = useDebouncedValue(
    fetchInput,
    150,
    (v) => `${v.fromPicker?.valueOf()}|${v.vesselId}|${v.enabled}`,
  )

  const {
    meteotrackerWaypointsGeojson: meteotrackerReportedWaypointsGeojson,
    loadingState: fetchingReported,
  } = useFetchWaypointsGeojson(
    debouncedInput.fromPicker,
    "reported",
    reportedRefreshTick,
    debouncedInput.vesselId,
    debouncedInput.enabled,
  )

  // A stable "now" snapshot (only changes when the reported data does) keeps the
  // worker from re-running on every render.
  const reportedToTs = useMemo(
    () => nowUtc(),
    [meteotrackerReportedWaypointsGeojson],
  )
  const workerInput = debouncedInput.enabled
    ? meteotrackerReportedWaypointsGeojson
    : null
  const { analysedReportedRoutes, loading: loadingReportedAnalysis } =
    useAnalyseReportedRoutesWorker(
      debouncedInput.fromPicker,
      reportedToTs,
      workerInput,
    )

  const vesselReportedRoutes = useFilterMarkedRoutes(
    analysedReportedRoutes,
    markedVesselNames,
  )

  // Days covered by the fetch window, for the tree row label ("12-day report").
  const reportedWindowDays = useMemo(() => {
    if (!fromPicker?.isValid?.()) return null
    return Math.max(1, Math.ceil(nowUtc().diff(fromPicker, "day", true)))
  }, [fromPicker])

  return {
    selectedVesselId,
    switchReported,
    reportedWindowDays,
    meteotrackerReportedWaypointsGeojson,
    fetchingReported,
    loadingReportedAnalysis,
    analysedReportedRoutes,
    vesselReportedRoutes,
  }
}
